import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CurrentBrandScope,
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  DYNAMIC_COMPONENT_DATA,
  ISidebarComponentData,
} from '@principle-theorem/ng-shared';
import {
  Appointment,
  AppointmentDeposit,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  ChecklistType,
  type IAppointment,
  type IAutomation,
  type IChecklistItem,
  type IEvent,
  type ILabJob,
  type IPatient,
  type IStaffer,
  type ITreatmentPlan,
  type IWaiveDepositRequest,
  TreatmentPlanStatus,
  type TreatmentStepAutomation,
  AppointmentSummary,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  httpsCallable,
  isPathChanged$,
  serialise,
  shareReplayCold,
  snapshot,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import { type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppointmentAutomationsDialogComponent } from '../scheduling/appointment-automations-dialog/appointment-automations-dialog.component';
import { type IWaiveDepositDialogResult } from './appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.component';
import {
  COULD_NOT_OPEN_DEPOSIT_MESSAGE,
  TakeDepositDialog,
  TakeDepositOpenResult,
} from './take-deposit-dialog';
import { TakeDepositEntry } from './take-deposit-entry';

export interface IAppointmentSidebarData extends ISidebarComponentData {
  appointment: AppointmentSummary;
}

@Component({
  selector: 'pr-appointment-view-sidebar',
  templateUrl: './appointment-view-sidebar.component.html',
  styleUrls: ['./appointment-view-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppointmentViewSidebarComponent {
  private _global = inject(GlobalStoreService);
  private _organisation = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);
  readonly depositsEnabled$ = inject(CurrentBrandScope).depositsEnabled$;
  treatmentStatuses = [
    TreatmentPlanStatus.Draft,
    TreatmentPlanStatus.Offered,
    TreatmentPlanStatus.Accepted,
    TreatmentPlanStatus.InProgress,
    TreatmentPlanStatus.Completed,
  ];
  appointment$: Observable<WithRef<IAppointment>>;
  patient$: Observable<WithRef<IPatient>>;
  currentPractitioner$: Observable<WithRef<IStaffer>>;
  treatmentPlan$: Observable<WithRef<ITreatmentPlan>>;
  event$: Observable<IEvent>;
  labJobs$: Observable<WithRef<ILabJob>[]>;
  automations$: Observable<WithRef<IAutomation<TreatmentStepAutomation>>[]>;
  checklistItems$: Observable<IChecklistItem[]>;
  canOverrideDeposit$: Observable<boolean>;

  constructor(
    @Inject(DYNAMIC_COMPONENT_DATA) data: IAppointmentSidebarData,
    private _dialog: MatDialog
  ) {
    this.canOverrideDeposit$ = this._organisation.userHasPermission$(
      AppointmentPermissions.DepositOverride
    );

    if (!data.appointment.ref) {
      return;
    }
    this.appointment$ = Firestore.doc$(data.appointment.ref).pipe(
      shareReplayCold()
    );
    this.patient$ = this.appointment$.pipe(
      switchMap((appointment) => Appointment.patient$(appointment)),
      shareReplayCold()
    );
    this.currentPractitioner$ = this.appointment$.pipe(
      switchMap((appointment) =>
        this._global.getStaffer$(appointment.practitioner.ref)
      )
    );
    this.event$ = this.appointment$.pipe(
      map((appointment) => appointment.event),
      filterUndefined()
    );
    this.treatmentPlan$ = this.appointment$.pipe(
      isPathChanged$('ref.id'),
      switchMap((appointment) => Appointment.treatmentPlan$(appointment))
    );
    this.automations$ = this.appointment$.pipe(
      isPathChanged$('ref.id'),
      switchMap((appointment) => Appointment.treatmentStep(appointment)),
      switchMap((step) => TreatmentStep.automations$(step))
    );
    this.checklistItems$ = this.appointment$.pipe(
      switchMap((appointment) =>
        Appointment.checklistItems$(appointment, ChecklistType.Pre)
      )
    );
    this.labJobs$ = this._getLabJobs$();
  }

  async openAutomations(): Promise<void> {
    const appointment = await snapshot(this.appointment$);
    const config = DialogPresets.medium({
      data: {
        appointment,
      },
    });
    this._dialog.open(AppointmentAutomationsDialogComponent, config);
  }

  async waiveDeposit(
    appointment: WithRef<IAppointment>,
    result: IWaiveDepositDialogResult
  ): Promise<void> {
    if (!AppointmentDeposit.canWaive(appointment.deposit)) {
      return;
    }
    const canOverrideDeposit = await snapshot(this.canOverrideDeposit$);
    if (!canOverrideDeposit) {
      return;
    }
    await snapshot(
      httpsCallable<SerialisedData<IWaiveDepositRequest>, void>(
        'http-stripe-waiveDeposit'
      )(
        serialise({
          appointmentRef: appointment.ref,
          waiverReason: result.waiverReason,
        })
      )
    );
  }

  async unwaiveDeposit(appointment: WithRef<IAppointment>): Promise<void> {
    const deposit = AppointmentDeposit.reinstate(appointment.deposit);
    if (!deposit) {
      return;
    }
    const canOverrideDeposit = await snapshot(this.canOverrideDeposit$);
    if (!canOverrideDeposit) {
      return;
    }
    await Firestore.updateDoc(appointment.ref, {
      deposit,
    });
  }

  /**
   * Hands capture to the same dialog every other surface uses.
   *
   * This card used to host the capture panel inline and carry its own copy of
   * the payment-link delivery chain. Routing through the shared opener keeps
   * one implementation, and keeps a flow that mutates deposit status out of a
   * card whose own visibility depends on it.
   */
  async takeDeposit(appointment: WithRef<IAppointment>): Promise<void> {
    const openResult = await TakeDepositDialog.open(
      this._dialog,
      appointment,
      TakeDepositEntry.Revisit
    );
    if (openResult !== TakeDepositOpenResult.Opened) {
      this._snackBar.open(COULD_NOT_OPEN_DEPOSIT_MESSAGE);
    }
  }

  private _getLabJobs$(): Observable<WithRef<ILabJob>[]> {
    return this.appointment$.pipe(
      switchMap((appointment) => Appointment.labJobs$(appointment)),
      shareReplayCold()
    );
  }
}
